import { Box, Button, CircularProgress, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import {
  getJobProgressSummary,
  getPrepareResultItems,
  getStatusItems,
} from './formatUtils'
import { getPrepareResults, startPrepare } from './utils'
import {
  pipelineErrorAtom,
  prepareResultAtom,
  prepareStatusAtom,
} from './atoms'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'

import { FlexBoxHelper } from './FlexBoxHelper'
import { JobProgressBar } from './JobProgressBar'

export function PrepareStage({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const status = useAtomValue(prepareStatusAtom)
  const pipelineError = useAtomValue(pipelineErrorAtom)
  const prepareResult = useAtomValue(prepareResultAtom)
  const setPipelineError = useSetAtom(pipelineErrorAtom)
  const setPrepareResult = useSetAtom(prepareResultAtom)

  const handleStartStage = async () => {
    try {
      await startPrepare(treeId, versionId)
      // The PipelineStatus component will pick up the new job status via polling
    } catch (error) {
      setPipelineError(
        `Error starting prepare stage: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  // Fetch prepare results when status is completed, no error, and result is null
  useEffect(() => {
    const fetchResults = async () => {
      if (!pipelineError && status?.flow_status?.completed && !prepareResult) {
        try {
          const results = await getPrepareResults(treeId, versionId)
          if (results.data?.results?.[0]) {
            setPrepareResult(results.data.results[0])
          }
        } catch (error) {
          setPipelineError(
            `Error fetching prepare results: ${error instanceof Error ? error.message : 'Unknown error'}`,
          )
        }
      }
    }

    fetchResults()
  }, [
    pipelineError,
    status?.flow_status?.completed,
    prepareResult,
    treeId,
    versionId,
    setPrepareResult,
    setPipelineError,
  ])

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Typography variant="h6" gutterBottom sx={{ fontStyle: 'italic' }}>
        Prepare
      </Typography>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleStartStage}
          disabled={
            pipelineError !== null || status?.flow_status?.completed === true
          }
          size="small"
        >
          Start
        </Button>
      </Box>

      {status && (
        <FlexBoxHelper
          header={
            !status?.flow_status?.completed ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} color="primary" />
                <Typography variant="body2" color="primary">
                  {status.flow_status
                    ? getJobProgressSummary(status.flow_status)
                    : 'Processing...'}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                color="success.main"
                sx={{ fontWeight: 'bold' }}
              >
                ✓ Completed Successfully
              </Typography>
            )
          }
          items={getStatusItems(status, null)}
        />
      )}

      {status?.flow_status && !status.flow_status.completed && (
        <JobProgressBar flowStatus={status.flow_status} />
      )}

      {prepareResult && (
        <FlexBoxHelper
          header={<Typography variant="h6">Prepare Results</Typography>}
          items={getPrepareResultItems(prepareResult)}
        />
      )}
    </Box>
  )
}
